import React from 'react';
import styled from 'styled-components';

const Shell = styled.div`
  align-self: stretch;
  min-height: 0;
  display: flex;
  flex-direction: row;
  position: relative;
  overflow: hidden;
  min-width: 0;
  flex: 0 0 auto;
  height: 100%;
  width: ${({ $collapsed, $expandedWidth, $railWidth, $persistentRail }) => {
    if ($collapsed) return `${$railWidth}px`;
    if ($persistentRail) return 'auto';
    return $expandedWidth;
  }};
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: ${({ $side }) => ($side === 'left' ? '1px solid rgba(0,0,0,0.1)' : 'none')};
  border-left: ${({ $side }) => ($side === 'right' ? '1px solid rgba(0,0,0,0.1)' : 'none')};
  background: rgba(255, 255, 255, 0.95);
`;

const Inner = styled.div`
  flex: ${({ $collapsed, $persistentRail, $expandedWidth }) => {
    if ($collapsed) return '0 0 0';
    if ($persistentRail) return `0 0 ${$expandedWidth}`;
    return '1 1 auto';
  }};
  width: ${({ $collapsed, $persistentRail, $expandedWidth }) => {
    if ($collapsed) return '0';
    if ($persistentRail) return $expandedWidth;
    return 'auto';
  }};
  min-height: 0;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: opacity 0.15s ease, flex-basis 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  ${({ $collapsed, $persistentRail }) =>
    $collapsed &&
    `
    opacity: 0;
    pointer-events: none;
    ${$persistentRail ? '' : 'position: absolute; inset: 0;'}
  `}
`;

const Rail = styled.div`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  width: ${({ $railWidth }) => `${$railWidth}px`};
  flex: 0 0 ${({ $railWidth }) => `${$railWidth}px`};
  height: 100%;
  padding: 8px 0;
  gap: 4px;
  background: #fff;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
`;

const RailBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active ? 'rgba(233,112,36,0.15)' : 'transparent')};
  color: #e97024;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  transition: background 0.15s ease;

  &:hover {
    background: #ffefe7;
  }

  &:focus-visible {
    outline: 2px solid #e97024;
    outline-offset: -2px;
  }
`;

const Grip = styled.span`
  width: 6px;
  height: 100%;
  background: transparent;
  border-radius: 3px;
  opacity: 0;
  transition: opacity 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
  pointer-events: none;
`;

const GripZone = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20px;
  z-index: 20;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'flex')};
  align-items: stretch;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  appearance: none;

  ${({ $side }) =>
    $side === 'left'
      ? 'right: 0; justify-content: flex-end;'
      : 'left: 0; justify-content: flex-start;'}

  &:hover ${Grip},
  &:focus-visible ${Grip} {
    opacity: 1;
    background: #e97024;
    box-shadow: 0 0 10px rgba(233, 112, 36, 0.55), 0 0 20px rgba(233, 112, 36, 0.25);
  }

  &:focus-visible {
    outline: none;
  }
`;

export default function CollapsibleSideShell({
  side = 'left',
  collapsed,
  onToggle,
  onExpand,
  expandedWidth = 'clamp(260px, 28vw, 360px)',
  railWidth = 50,
  railIcon,
  railActions,
  activeRailId,
  persistentRail = false,
  collapseLabel = 'Collapse panel',
  children,
}) {
  const showRail = Boolean(railActions?.length) || Boolean(railIcon);
  const railVisible = showRail && (collapsed || persistentRail);

  return (
    <Shell
      $side={side}
      $collapsed={collapsed}
      $expandedWidth={expandedWidth}
      $railWidth={railWidth}
      $persistentRail={persistentRail}
    >
      {side === 'right' && (
        <GripZone
          type="button"
          $side={side}
          $collapsed={collapsed}
          onClick={onToggle}
          aria-label={collapseLabel}
        >
          <Grip aria-hidden="true" />
        </GripZone>
      )}

      <Inner
        $collapsed={collapsed}
        $persistentRail={persistentRail}
        $expandedWidth={expandedWidth}
      >
        {children}
      </Inner>

      <Rail $visible={railVisible} $railWidth={railWidth}>
        {railActions?.length ? railActions.map((action) => (
          <RailBtn
            key={action.id}
            type="button"
            $active={activeRailId === action.id}
            onClick={action.onClick}
            title={action.label}
            aria-label={action.label}
          >
            {action.icon}
          </RailBtn>
        )) : (
          <RailBtn type="button" onClick={onExpand} aria-label="Expand panel">
            {railIcon}
          </RailBtn>
        )}
      </Rail>

      {side === 'left' && (
        <GripZone
          type="button"
          $side={side}
          $collapsed={collapsed}
          onClick={onToggle}
          aria-label={collapseLabel}
        >
          <Grip aria-hidden="true" />
        </GripZone>
      )}
    </Shell>
  );
}
